import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getStock, exportStockXlsx, adjustStock } from '../../api/inventory';
import { getCatalog, getCategory } from '../../api/catalog';
import './AdminInventory.css';
import { deleteProduct, setProductActive } from '../../api/admin/products.admin';
import AdminProductEdit from './AdminProductEdit';
import { useAuth } from '../../context/AuthContext';
import { csrf } from '../../api/auth';

function ProductActions({ p, isAdmin, deletedMode, onChanged, onAddStock, onEdit }) {
  if (!isAdmin) return <span className="muted">—</span>;
  if (deletedMode) {
    return (
      <div className="inventory-row-actions">
        <button type="button" className="edit-btn" onClick={() => onEdit(p)}>
          Открыть
        </button>
        <button
          type="button"
          className="edit-btn"
          onClick={async () => {
            try {
              await setProductActive(p._id, true);
              onChanged();
            } catch (e) {
              alert(e?.response?.data?.error || 'Не удалось восстановить');
            }
          }}
        >
          Восстановить
        </button>
      </div>
    );
  }
  return (
    <div className="inventory-row-actions">
      <button type="button" className="edit-btn" onClick={() => onAddStock(p)}>
        Добавить
      </button>
      <button type="button" className="edit-btn" onClick={() => onEdit(p)}>
        Редактировать
      </button>
      <button
        type="button"
        className="muted"
        onClick={async () => {
          if (!window.confirm(`Переместить «${p.title}» в удалённые?`)) return;
          try {
            await deleteProduct(p._id);
            onChanged();
          } catch (e) {
            alert(e?.response?.data?.error || 'Не удалось удалить');
          }
        }}
      >
        Удалить
      </button>
    </div>
  );
}

function ProductThumb({ src, title }) {
  if (!src) return <div className="inv-thumb inv-thumb--empty" aria-hidden="true" />;
  return (
    <div className="inv-thumb">
      <img src={src} alt={title || ''} loading="lazy" />
    </div>
  );
}

function ProductCell({ p }) {
  return (
    <div className="inv-product">
      <ProductThumb src={p.image} title={p.title} />
      <span className="inv-product__title">{p.title}</span>
    </div>
  );
}

export default function AdminInventory() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [raw, setRaw] = useState([]);
  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [groupByBrands, setGroupByBrands] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  const [filters, setFilters] = useState({
    q: '',
    topCategorySlug: '',
    subcategoryId: '',
    brand: '',
  });

  const [catalog, setCatalog] = useState([]);
  const [subcats, setSubcats] = useState([]);

  const [popupOpen, setPopupOpen] = useState(false);
  const [pickerList, setPickerList] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [popupQ, setPopupQ] = useState('');
  const [popupCat, setPopupCat] = useState('');
  const [popupSub, setPopupSub] = useState('');
  const [popupSubcats, setPopupSubcats] = useState([]);
  const [receiptProductId, setReceiptProductId] = useState('');
  const [receiptQty, setReceiptQty] = useState(1);
  const [receiptSaving, setReceiptSaving] = useState(false);
  const [receiptMsg, setReceiptMsg] = useState('');
  const [editId, setEditId] = useState(null);

  const filtersRef = useRef(filters);
  const groupByBrandsRef = useRef(groupByBrands);
  const showDeletedRef = useRef(showDeleted);
  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { groupByBrandsRef.current = groupByBrands; }, [groupByBrands]);
  useEffect(() => { showDeletedRef.current = showDeleted; }, [showDeleted]);

  const load = useCallback(async () => {
    const params = {
      ...filtersRef.current,
      groupBy: groupByBrandsRef.current ? 'brand' : '',
    };
    if (showDeletedRef.current) params.deleted = 'true';
    const data = await getStock(params);
    setRaw(Array.isArray(data) ? data : []);
    const flat = Array.isArray(data) && data.length && data[0]?.items
      ? data.flatMap((g) => g.items)
      : Array.isArray(data) ? data : [];
    setItems(flat);
  }, []);

  // Чекбокс «По брендам» — сразу перезагружаем список
  useEffect(() => {
    load();
  }, [groupByBrands, showDeleted, load]);

  useEffect(() => {
    const uniq = Array.from(
      new Set(items.map((p) => (p.brand || '').trim()).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b, 'ru'));
    setBrands(uniq);
  }, [items]);

  const loadCatalog = useCallback(async () => {
    const data = await getCatalog();
    setCatalog(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  const loadPicker = useCallback(async (opts = {}) => {
    setPickerLoading(true);
    try {
      const data = await getStock({
        q: opts.q ?? '',
        topCategorySlug: opts.topCategorySlug ?? '',
        subcategoryId: opts.subcategoryId ?? '',
      });
      const flat = Array.isArray(data) && data.length && data[0]?.items
        ? data.flatMap((g) => g.items)
        : Array.isArray(data) ? data : [];
      flat.sort((a, b) => (a.title || '').localeCompare(b.title || '', 'ru'));
      setPickerList(flat);
    } finally {
      setPickerLoading(false);
    }
  }, []);

  async function openPopup(preselect = null) {
    setReceiptMsg('');
    setReceiptQty(1);
    setPopupQ('');
    setPopupCat('');
    setPopupSub('');
    setPopupSubcats([]);
    setReceiptProductId(preselect?._id ? String(preselect._id) : '');
    setPickerList(preselect?._id ? [preselect] : []);
    setPopupOpen(true);
  }

  const closePopup = useCallback(() => {
    if (receiptSaving) return;
    setPopupOpen(false);
    setReceiptMsg('');
  }, [receiptSaving]);

  async function onPopupCatChange(e) {
    const slug = e.target.value;
    setPopupCat(slug);
    setPopupSub('');
    if (!slug) {
      setPopupSubcats([]);
      await loadPicker({ q: popupQ, topCategorySlug: '', subcategoryId: '' });
      return;
    }
    const cat = await getCategory(slug);
    setPopupSubcats(cat?.subcategories || []);
    await loadPicker({ q: popupQ, topCategorySlug: slug, subcategoryId: '' });
  }

  async function onPopupSubChange(e) {
    const id = e.target.value;
    setPopupSub(id);
    await loadPicker({ q: popupQ, topCategorySlug: popupCat, subcategoryId: id });
  }

  async function onPopupSearch() {
    await loadPicker({ q: popupQ, topCategorySlug: popupCat, subcategoryId: popupSub });
  }

  async function exportXlsx() {
    try {
      const blob = await exportStockXlsx({
        ...filters,
        ...(showDeleted ? { deleted: 'true' } : {}),
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.message || 'Не удалось выгрузить Excel');
    }
  }

  async function handleCategoryChange(e) {
    const slug = e.target.value;
    setFilters((f) => ({ ...f, topCategorySlug: slug, subcategoryId: '' }));
    if (!slug) return setSubcats([]);
    const cat = await getCategory(slug);
    setSubcats(cat?.subcategories || []);
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  }

  async function submitReceipt(e) {
    e.preventDefault();
    setReceiptMsg('');
    const qty = Math.floor(Number(receiptQty));
    if (!receiptProductId) {
      setReceiptMsg('Выберите товар');
      return;
    }
    if (!Number.isFinite(qty) || qty < 1) {
      setReceiptMsg('Укажите количество в шт (не меньше 1)');
      return;
    }
    setReceiptSaving(true);
    try {
      await csrf();
      await adjustStock(receiptProductId, qty, 'Приход на склад');
      setReceiptMsg(`Добавлено ${qty} шт`);
      setReceiptQty(1);
      await load();
      setTimeout(() => {
        setPopupOpen(false);
        setReceiptMsg('');
      }, 600);
    } catch (err) {
      setReceiptMsg(err?.response?.data?.error || 'Не удалось добавить на склад');
    } finally {
      setReceiptSaving(false);
    }
  }

  const selectedProduct = useMemo(
    () => pickerList.find((p) => String(p._id) === String(receiptProductId)),
    [pickerList, receiptProductId]
  );

  const isGrouped = useMemo(() => !!(raw[0]?.items), [raw]);

  useEffect(() => {
    if (!popupOpen && !editId) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (editId) setEditId(null);
      else closePopup();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [popupOpen, editId, closePopup]);

  return (
    <div className="inventory-container">
      <div className="inventory-head">
        <h2 className="inventory-title admin-page-title">
          {showDeleted ? 'Удалённые товары' : 'Склад / Остатки'}
        </h2>
        <div className="inventory-head-actions">
          <button
            type="button"
            className={`filtr${showDeleted ? ' is-on' : ''}`}
            onClick={() => setShowDeleted((v) => !v)}
          >
            {showDeleted ? 'К складу' : 'Показать удалённые'}
          </button>
          {isAdmin && !showDeleted && (
            <button type="button" className="filtr inventory-add-btn" onClick={() => openPopup()}>
              Добавить товар
            </button>
          )}
        </div>
      </div>

      <div className="inventory-filters">
        <input
          className="fld"
          name="q"
          placeholder="Поиск"
          value={filters.q}
          onChange={handleChange}
        />

        <select
          className="fld"
          name="topCategorySlug"
          value={filters.topCategorySlug}
          onChange={handleCategoryChange}
        >
          <option value="">Все категории</option>
          {catalog.map((c) => (
            <option key={c._id} value={c.slug}>{c.name?.ru || c.slug}</option>
          ))}
        </select>

        <select
          className="fld"
          name="subcategoryId"
          value={filters.subcategoryId}
          onChange={handleChange}
          disabled={!subcats.length}
        >
          <option value="">Все подкатегории</option>
          {subcats.map((s) => (
            <option key={s._id} value={s._id}>{s.name?.ru || s.slug}</option>
          ))}
        </select>

        <select
          className="fld"
          name="brand"
          value={filters.brand}
          onChange={handleChange}
        >
          <option value="">Все бренды</option>
          {brands.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>

        <label className="inventory-check">
          <input
            type="checkbox"
            checked={groupByBrands}
            onChange={(e) => setGroupByBrands(e.target.checked)}
          />
          <span>По брендам</span>
        </label>

        <button type="button" className="excel" onClick={exportXlsx}>Excel</button>
        <button type="button" className="filtr" onClick={load}>Фильтровать</button>
      </div>

      {!isGrouped && (
        <table className="inventory-table">
          <thead>
            <tr>
              <th>Артикул</th>
              <th>Товар</th>
              <th>Бренд</th>
              <th>Склад, шт</th>
              <th>Резерв</th>
              <th>Свободно</th>
              <th>Цена (розн.)</th>
              <th>Цена (опт)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {raw.length === 0 && (
              <tr>
                <td colSpan={9} className="muted">
                  {showDeleted ? 'Удалённых товаров нет' : 'Товаров нет'}
                </td>
              </tr>
            )}
            {raw.map((p) => (
              <tr key={p._id}>
                <td>{p.sku || '—'}</td>
                <td className="inv-td-product"><ProductCell p={p} /></td>
                <td>{p.brand || '—'}</td>
                <td>{p.stockQty}</td>
                <td>{p.reservedQty || 0}</td>
                <td><b>{p.availableQty ?? Math.max(0, (p.stockQty || 0) - (p.reservedQty || 0))}</b></td>
                <td>{p.priceRetail}</td>
                <td>{p.priceWholesale}</td>
                <td>
                  <ProductActions
                    p={p}
                    isAdmin={isAdmin}
                    deletedMode={showDeleted}
                    onChanged={load}
                    onAddStock={openPopup}
                    onEdit={(p) => setEditId(p._id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {isGrouped && raw.length === 0 && (
        <p className="muted">{showDeleted ? 'Удалённых товаров нет' : 'Товаров нет'}</p>
      )}

      {isGrouped && (
        <div className="inventory-groups">
          {raw.map((group) => (
            <div key={group.brand} className="inventory-group">
              <h3 style={{ margin: '16px 0 8px' }}>{group.brand}</h3>
              <table className="inventory-table">
                <thead>
                  <tr>
                    <th>Артикул</th>
                    <th>Товар</th>
                    <th>Склад, шт</th>
                    <th>Резерв</th>
                    <th>Свободно</th>
                    <th>Цена (розн.)</th>
                    <th>Цена (опт)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((p) => (
                    <tr key={p._id}>
                      <td>{p.sku || '—'}</td>
                      <td className="inv-td-product"><ProductCell p={p} /></td>
                      <td>{p.stockQty}</td>
                      <td>{p.reservedQty || 0}</td>
                      <td><b>{p.availableQty ?? Math.max(0, (p.stockQty || 0) - (p.reservedQty || 0))}</b></td>
                      <td>{p.priceRetail}</td>
                      <td>{p.priceWholesale}</td>
                      <td>
                        <ProductActions
                          p={p}
                          isAdmin={isAdmin}
                          deletedMode={showDeleted}
                          onChanged={load}
                          onAddStock={openPopup}
                          onEdit={(p) => setEditId(p._id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {popupOpen && (
        <div className="inv-modal" role="dialog" aria-modal="true" aria-labelledby="inv-modal-title">
          <div className="inv-modal__backdrop" aria-hidden="true" onClick={closePopup} />
          <div className="inv-modal__panel">
            <div className="inv-modal__head">
              <h3 id="inv-modal-title">Приход на склад</h3>
              <button type="button" className="inv-modal__close" onClick={closePopup} aria-label="Закрыть">✕</button>
            </div>

            <div className="inv-modal__filters">
              <input
                className="fld"
                placeholder="Поиск товара"
                value={popupQ}
                onChange={(e) => setPopupQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onPopupSearch();
                  }
                }}
              />
              <select className="fld" value={popupCat} onChange={onPopupCatChange}>
                <option value="">Все категории</option>
                {catalog.map((c) => (
                  <option key={c._id} value={c.slug}>{c.name?.ru || c.slug}</option>
                ))}
              </select>
              <select
                className="fld"
                value={popupSub}
                onChange={onPopupSubChange}
                disabled={!popupSubcats.length}
              >
                <option value="">Все подкатегории</option>
                {popupSubcats.map((s) => (
                  <option key={s._id} value={s._id}>{s.name?.ru || s.slug}</option>
                ))}
              </select>
              <button type="button" className="filtr" onClick={onPopupSearch}>Найти</button>
            </div>

            <form className="inv-modal__form" onSubmit={submitReceipt}>
              <label>
                Товар
                <select
                  className="fld"
                  value={receiptProductId}
                  onChange={(e) => setReceiptProductId(e.target.value)}
                  required
                  disabled={pickerLoading}
                >
                  <option value="">{pickerLoading ? 'Загрузка…' : '— найдите и выберите товар —'}</option>
                  {pickerList.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.sku ? `[${p.sku}] ` : ''}{p.title}
                      {p.brand ? ` · ${p.brand}` : ''}
                      {` · на складе ${p.stockQty ?? 0} шт`}
                    </option>
                  ))}
                </select>
              </label>

              {selectedProduct && (
                <div className="inv-modal__picked">
                  <ProductThumb src={selectedProduct.image} title={selectedProduct.title} />
                  <div>
                    <div className="inv-modal__picked-title">{selectedProduct.title}</div>
                    <div>
                      Сейчас: <b>{selectedProduct.stockQty ?? 0}</b> шт
                      {selectedProduct.reservedQty ? ` (резерв ${selectedProduct.reservedQty})` : ''}
                    </div>
                  </div>
                </div>
              )}

              <label>
                Количество, шт
                <input
                  className="fld"
                  type="number"
                  min="1"
                  step="1"
                  value={receiptQty}
                  onChange={(e) => setReceiptQty(e.target.value === '' ? '' : Number(e.target.value))}
                  required
                />
              </label>

              {receiptMsg && <div className="inv-modal__msg">{receiptMsg}</div>}

              <div className="inv-modal__actions">
                <button type="button" className="muted" onClick={closePopup} disabled={receiptSaving}>
                  Отмена
                </button>
                <button type="submit" className="filtr" disabled={receiptSaving}>
                  {receiptSaving ? 'Сохраняю…' : 'Добавить на склад'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editId && (
        <div className="inv-modal" role="dialog" aria-modal="true" aria-labelledby="inv-edit-title">
          <div className="inv-modal__backdrop" aria-hidden="true" onClick={() => setEditId(null)} />
          <div className="inv-modal__panel inv-modal__panel--edit">
            <div className="inv-modal__head">
              <h3 id="inv-edit-title">Редактирование товара</h3>
              <button type="button" className="inv-modal__close" onClick={() => setEditId(null)} aria-label="Закрыть">✕</button>
            </div>
            <AdminProductEdit
              productId={editId}
              embedded
              onClose={() => setEditId(null)}
              onSaved={load}
            />
          </div>
        </div>
      )}
    </div>
  );
}
